import { BaseAPIService } from '../api/base';
import { supabase } from '../api/client';

/**
 * Post Service
 * Handles all community post-related API calls
 */
class PostService extends BaseAPIService {
  constructor() {
    super('posts');
  }

  /**
   * Get posts by board type
   */
  async getPostsByBoard(boardType, options = {}) {
    try {
      let query = supabase
        .from('posts')
        .select('*, profiles!posts_user_id_fkey(full_name, avatar_url)')
        .eq('board_type', boardType);

      // Apply filters
      if (options.category) {
        query = query.eq('category', options.category);
      }

      // Apply search
      if (options.search) {
        query = query.or(
          `title.ilike.%${options.search}%,content.ilike.%${options.search}%`
        );
      }

      // Apply ordering
      query = query.order('created_at', { ascending: false });

      // Apply pagination
      if (options.page && options.limit) {
        const start = (options.page - 1) * options.limit;
        query = query.range(start, start + options.limit - 1);
      }

      const { data, error, count } = await query;

      if (error) throw error;
      return { success: true, data, count };
    } catch (error) {
      console.error('Error fetching posts:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get post by ID
   */
  async getPost(postId) {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*, profiles!posts_user_id_fkey(full_name, avatar_url)')
        .eq('id', postId)
        .single();

      if (error) throw error;

      // Increment view count
      await this.incrementViewCount(postId);

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching post:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create new post
   */
  async createPost(postData) {
    try {
      const { data, error } = await supabase
        .from('posts')
        .insert([postData])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error creating post:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update post
   */
  async updatePost(postId, updates) {
    return this.update(postId, updates);
  }

  /**
   * Delete post
   */
  async deletePost(postId) {
    return this.delete(postId);
  }

  /**
   * Increment view count
   */
  async incrementViewCount(postId) {
    try {
      const { error } = await supabase.rpc('increment_view_count', {
        post_id: postId,
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error incrementing view count:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Toggle like
   */
  async toggleLike(postId, userId) {
    try {
      // Check if already liked
      const { data: existing } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .single();

      if (existing) {
        // Unlike
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('id', existing.id);

        if (error) throw error;
        return { success: true, liked: false };
      } else {
        // Like
        const { error } = await supabase
          .from('post_likes')
          .insert([{ post_id: postId, user_id: userId }]);

        if (error) throw error;
        return { success: true, liked: true };
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get comments for a post
   */
  async getComments(postId) {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*, profiles!comments_user_id_fkey(full_name, avatar_url)')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching comments:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add comment
   */
  async addComment(commentData) {
    try {
      const { data, error } = await supabase
        .from('comments')
        .insert([commentData])
        .select('*, profiles!comments_user_id_fkey(full_name, avatar_url)')
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding comment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete comment
   */
  async deleteComment(commentId) {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting comment:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get popular posts
   */
  async getPopularPosts(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, view_count, like_count, comment_count')
        .order('view_count', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching popular posts:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export const postService = new PostService();
export default postService;